import React, { useState, useEffect } from 'react';
import {debounce, filter, orderBy, reject} from 'lodash';

import { withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import Slide from '@material-ui/core/Slide';
import Alert from '@material-ui/lab/Alert';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';

import {onlineHubHost} from '../../config';
import { useFetchResource } from '../../helpers/hooks';

import IconInput from '../IconInput';
import Table from '../Table';
import FirmwareForm from './components/FirmwareForm';

import './FirmwareList.css';

const NewButton = withStyles((theme) => ({
  root: {
    margin: theme.spacing(1)
  }
}))(Button);

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

const columns = [
  { id: 'product', label: 'Product', sort: true, minWidth: 60 },
  { id: 'target', label: 'Target', sort: true, minWidth: 80 },
  { id: 'version', label: 'Version', sort: true, minWidth: 60 },
  { id: 'url', label: 'Download', sort: true, minWidth: 200, link: true }
];

function sortItems(items, sortBy, sortOrder) {
  if (sortBy === 'url') {
    return orderBy(items, [item => item.payload && item.payload.bDeploy.url.toLowerCase()], [sortOrder]);
  }
  return orderBy(items, [item => item[sortBy].toLowerCase()], [sortOrder]);
}

function searchItems(items, search) {
  const normalizedSearch = search.toLowerCase();
  return filter(items, item => (
    item.target.toLowerCase().includes(normalizedSearch)
    || item.version.toLowerCase().includes(normalizedSearch)
  ))
}

function getFormData({firmwareFile, ...data}) {
  const formData = new FormData();
  Object.keys(data).forEach((key) => {
    formData.append(key, data[key]);
  });
  formData.append('file', firmwareFile);
  return formData;
}

export default function FirmwareList() {
  const [sortBy, setSortBy] = useState('product');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortedItems, setSortedItems] = useState(null);
  const [search, setSearch] = useState('');
  const [filteredItems, setFilteredItems] = useState(null);
  const [newModalActive, setNewModalActive] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState({});

  const {data} = useFetchResource(`${onlineHubHost}/app/firmwares`);
  const items = filteredItems || sortedItems || [];

  useEffect(() => {
    if (data && !sortedItems) {
      setSortedItems(sortItems(data || [], sortBy, sortOrder));
    }
  }, [data, sortedItems, sortBy, sortOrder])

  function handleSortClick(newSortBy) {
    if (newSortBy === sortBy) {
      const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newSortOrder);
      setSortedItems(sortItems(items, sortBy, newSortOrder));
    } else {
      setSortBy(newSortBy);
      setSortedItems(sortItems(items, newSortBy, sortOrder));
    }
  }

  const handleSearchChange = debounce((value) => {
    const source = sortedItems || data || [];
    setFilteredItems(value.length ? searchItems(source, value) : null);
    setSearch(value);
  }, 500)

  function handleNewClick() {
    setNewModalActive(!newModalActive);
  }

  function handleAddClose() {
    setNewModalActive(false);
    setEditItemId(null);
  }

  function handleAddSubmit({firmwareFile, ...data}) {
    const body = firmwareFile ? getFormData({firmwareFile, ...data}) : JSON.stringify(data);
    const headers = {
      authorization: `Bearer ${localStorage.getItem('token')}`
    };
    if (!firmwareFile) {
      headers['content-type'] = 'application/json';
    }
    const url = `${onlineHubHost}/app/firmwares${editItemId ? `/${editItemId}` : ''}`
    fetch(url, {
      method: !editItemId ? 'POST' : 'PATCH',
      headers,
      body
    })
    .then((res) => {
      if (res.ok) {
        return res.json();
      }
      throw new Error(`Unable to ${editItemId ? 'update' : 'add a'} firmware`);
    })
    .then((result) => {
      const newSortedItems = sortItems((editItemId
        ? reject(items, {id: editItemId})
        : items).concat(Array.isArray(result) ? result : [result])
      , sortBy, sortOrder);
      setSortedItems(newSortedItems);
      if (search) {
        setFilteredItems(searchItems(newSortedItems, search));
      }
      setNewModalActive(false);
      setEditItemId(null);
      setSnackbarMessage({show: true, severity: 'success', text: `Your firmware has been ${editItemId ? 'updated' : 'added'}.`});
    })
    .catch((err) => {
      setSnackbarMessage({show: true, severity: 'error', text: `Your firmware has not been ${editItemId ? 'updated' : 'added'}.`});
      console.error(err);
    })
  }

  function handleItemEdit(id) {
    setEditItemId(id);
    setNewModalActive(true);
  }

  function handleItemsDelete(ids) {
    fetch(`${onlineHubHost}/app/firmwares`, {
      method: 'DELETE',
      headers: {
        'content-type': 'application/json',
        authorization: `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify({ids})
    })
    .then((res) => {
      if (res.ok) {
        return res.json();
      }
      throw new Error('Unable to delete firmware(s)');
    })
    .then((result) => {
      const newSortedItems = sortItems(reject(items, item => result.ids.includes(item.id)), sortBy, sortOrder);
      setSortedItems(newSortedItems);
      if (search) {
        setFilteredItems(searchItems(newSortedItems, search));
      }
      setSnackbarMessage({show: true, severity: 'success', text: `Your firmware${ids.length > 1 ? 's have' : ' has'} been deleted.`});
    })
    .catch((err) => {
      setSnackbarMessage({show: true, severity: 'error', text: `Unable to delete ${ids.length ? 'those firmwares' : 'that firmware'}.`})
      console.error(err);
    })
  }

  return (
    <div className="FirmwareList">
      <div className="FirmwareList-header">
        <NewButton
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewClick}
        >
          Add
        </NewButton>
        <IconInput
          icon={<SearchIcon />}
          label="Search"
          onChange={handleSearchChange}
        />
      </div>
      <Table
        columns={columns}
        items={items}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortClick={handleSortClick}
        onEdit={handleItemEdit}
        onDelete={handleItemsDelete}
      />
      <FirmwareForm
        itemId={editItemId}
        items={items}
        active={newModalActive}
        onClose={handleAddClose}
        onSubmit={handleAddSubmit}
      />
      <Snackbar
        open={snackbarMessage.show}
        autoHideDuration={3000}
        TransitionComponent={SlideTransition}
        onClose={() => setSnackbarMessage({...snackbarMessage, show: false})}
      >
        <Alert severity={snackbarMessage.severity} onClose={() => setSnackbarMessage({...snackbarMessage, show: false})}>
          {snackbarMessage.text}
        </Alert>
      </Snackbar>
    </div>
  );
}
